import chunk from 'lodash/chunk';
import { requestStardogVoiceboxGenesis } from 'vet-bones/utils/voicebox';

import {
  MAX_GENERATE_QUERIES_CHUNK_SIZE,
  limitConcurrentGenerateQueries,
} from 'src/constants/limits';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { Project } from 'src/store/state/projects';
import { buildGenerateQueriesParameters } from 'src/utils/voicebox/hooks/parameters';
import {
  GenerateQueriesRequest,
  GenerateQueriesResponse,
} from 'src/utils/voicebox/hooks/types';

/**
 * Requests SPARQL queries for competency questions a few at a time instead of
 * all at once.
 *
 * Voicebox generates one query per question with a sequential LLM call, so a
 * single request for a whole project runs past the 60s gateway timeout and
 * every question fails together (VET-7434). Chunks are independent requests
 * server-side, so they also isolate failures to the questions in one chunk.
 *
 * Never rejects. A chunk that fails contributes its question ids to
 * `failedQuestionIds` and the other chunks still resolve, so callers decide how
 * to report partial failure.
 */
export const requestGenerateQueriesInChunks = async ({
  connectionIdx,
  endpoint,
  isStale,
  project,
  questions,
}: {
  connectionIdx: number;
  endpoint: string;
  /**
   * Checked before each chunk goes out, so an evaluation that has been
   * superseded stops sending requests instead of running every queued chunk to
   * completion. Questions in chunks that were never sent come back in
   * `failedQuestionIds`.
   */
  isStale?: () => boolean;
  project: Project;
  questions: CompetencyQuestion[];
}) => {
  const chunks = chunk(questions, MAX_GENERATE_QUERIES_CHUNK_SIZE);

  const settledChunks = await Promise.allSettled(
    chunks.map((chunkedQuestions) =>
      limitConcurrentGenerateQueries(async () => {
        if (isStale?.()) {
          return [];
        }

        const response = await requestStardogVoiceboxGenesis<
          GenerateQueriesRequest,
          GenerateQueriesResponse
        >({
          connectionIdx,
          body: {
            endpoint,
            action: 'generate_queries',
            parameters: buildGenerateQueriesParameters(
              project,
              chunkedQuestions
            ),
          },
        });

        // An error response has no `results`, and a malformed body may hold
        // anything; both collapse to "no queries for this chunk" rather than
        // throwing, or one bad response would fail the whole request.
        const generated = response.results?.questions;
        return Array.isArray(generated)
          ? generated.filter((res) => typeof res?.query === 'string')
          : [];
      })
    )
  );

  const generatedQuestions: GenerateQueriesResponse['questions'] = [];
  const failedQuestionIds: string[] = [];

  chunks.forEach((chunkedQuestions, idx) => {
    const settled = settledChunks[idx];
    // A rejected chunk means no queries for its questions, same as an error
    // response — it must not fail the whole request.
    const generatedInChunk =
      settled.status === 'fulfilled' ? settled.value : [];

    chunkedQuestions.forEach((question) => {
      const generated = generatedInChunk.find((res) => res.id === question.id);
      const query = generated?.query ?? '';

      // Voicebox answers with an empty query for a question whose SPARQL could
      // not be generated rather than failing the request (VET-7249), so an
      // empty query is a per-question failure and not a successful result.
      if (query.trim()) {
        generatedQuestions.push({ id: question.id, query });
      } else {
        failedQuestionIds.push(question.id);
      }
    });
  });

  return {
    results: { questions: generatedQuestions },
    failedQuestionIds,
  };
};
